import React, { useMemo } from 'react';
import { useToast } from '../../../providers/ToastProvider';
import Icon, { IconType } from '../../icons/Icon';

export enum ToastType {
  error = 'error',
  success = 'success',
}

export interface ToastInput {
  type: ToastType;
  title: string;
  content?: string | React.ReactNode | ((toastId: string) => React.ReactNode);
  onClick?: () => void;
  id?: string;
}

interface ToastProps extends ToastInput {
  id: string;
  offset?: number;
  position?: 'fixed' | 'absolute' | 'relative';
}

const leadingIcons = {
  error: <Icon type={IconType.infoHexagon} stroke={'stroke-error-500'} size={'h-6 w-6'} />,
  success: <Icon type={IconType.checkCircle} stroke={'stroke-success-500'} size={'h-6 w-6'} />,
} as const;

const roles = {
  error: 'alert',
  warm: 'status',
  success: 'status',
};

export default function Toast({ id, type, title, content, onClick, offset = 0, position = 'fixed' }: ToastProps) {
  const { closeToast } = useToast();

  const shownContent = useMemo(() => {
    if (!content) return null;
    if (typeof content === 'string') return <p className='px-8 text-gray-600'>{content}</p>;

    const wrap = (el: React.ReactNode) => {
      return <div className='px-8 text-gray-600'>{el}</div>;
    };
    if (typeof content === 'function') return wrap(content(id));
    return wrap(content);
  }, [content]);

  const cursorClass = useMemo(() => {
    if (onClick) return 'cursor-pointer';

    return '';
  }, [onClick]);

  const translation = useMemo(() => {
    if (position === 'fixed') return 'left-6';

    return '';
  }, []);

  const bottom = useMemo(() => {
    if (position === 'relative') return 0;
    return 64 + offset * 8;
  }, [offset]);

  const marginLeft = useMemo(() => {
    if (position === 'relative') return 0;
    return offset * 8;
  }, [offset]);

  const zIndex = useMemo(() => {
    if (position === 'relative') return 51;
    return 50 - offset;
  }, [offset]);

  return (
    <div
      className={`${cursorClass} ${position} ${translation} grid w-fit max-w-[400px] gap-1 rounded-2 bg-white px-4 py-4 text-sm shadow-default`}
      style={{ marginLeft, zIndex, bottom }}
      role={roles[type]}
      aria-live='polite'
      tabIndex={0}
      onClick={onClick}
    >
      <div className='flex max-w-[368px] items-center gap-2'>
        <div>{leadingIcons[type]}</div>
        <p className='flex-grow overflow-hidden break-words font-medium text-black'>{title}</p>
        <div>
          <Icon
            type={IconType.xClose}
            stroke={'stroke-gray-600'}
            size={'h-5 w-5'}
            onClick={event => {
              event.stopPropagation();
              event.preventDefault();
              closeToast(id);
            }}
          />
        </div>
      </div>
      {shownContent}
    </div>
  );
}
